import Box from '@material-ui/core/Box';
import GhostLoader from 'react-ghost-loader';
import Grid from '@material-ui/core/Grid';
import OtsSegmentDetailStatCards from '../otsSegmentDetailStatCards';
import OtsSegmentSizeRevenueChart from '../otsSegmentSizeRevenueChart';
import React, { useMemo } from 'react';
import { ArrowLeft, PencilSimple, Table } from '@phosphor-icons/react';
import { Button } from '@attain-sre/attain-design-system';
import {
  DESCRIPTION,
  NO_DATA,
  OTS_SGM_MNG_CREATE_NEW_AUDIENCE,
  OTS_SGM_MNG_EDIT_AUDIENCE,
  OTS_SGM_MNG_NO_DESCRIPTION_AVAILABLE,
  OTS_SGM_MNG_NO_RECORD_FOUND,
  OTS_SGM_MNG_SEGMENT_NOT_FOUND_MESSAGE,
} from '../../../content';
import { GET_TTD_OTS_SEGMENT_DETAIL_BY_ID } from '../../../graphql/queries';
import { KeyboardDoubleArrowRightOutlined } from '@mui/icons-material';
import { RateLevel, Routes } from '../../../constants';
import { SegmentDetail } from '../types';
import {
  buildSegmentQueryVariables,
  parseAndValidateSegmentParams,
} from '../utils/urlHelpers';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import * as Styled from './index.styles';

const OtsSegmentDetail: React.FC = () => {
  const { segmentId } = useParams<{
    platform: string;
    segmentId: string;
  }>();
  const history = useHistory();
  const location = useLocation();

  // Parse and validate URL parameters
  const urlValidation = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return parseAndValidateSegmentParams(searchParams, segmentId);
  }, [location.search, segmentId]);

  // Build GraphQL variables using helper (always call hooks before early returns)
  const queryVariables = useMemo(() => {
    if (!urlValidation.isValid || !urlValidation.params) return null;
    const { rateLevel, advertiserId, partnerId } = urlValidation.params;
    return buildSegmentQueryVariables(
      segmentId,
      rateLevel,
      advertiserId || undefined,
      partnerId || undefined
    );
  }, [urlValidation, segmentId]);

  const { data, error, loading } = useQuery<{
    ttdOtsSegmentDetailById: SegmentDetail;
  }>(GET_TTD_OTS_SEGMENT_DETAIL_BY_ID, {
    variables: queryVariables || {},
    skip: !queryVariables,
    fetchPolicy: 'network-only',
  });

  // If validation fails, show error page
  if (!urlValidation.isValid) {
    return (
      <Box>
        <Styled.HeaderContainer>
          <Styled.HeaderLeft>
            <Styled.BackButton
              onClick={() => history.push(Routes.OTS_SEGMENT_MANAGER)}
            >
              <ArrowLeft size={20} />
            </Styled.BackButton>
          </Styled.HeaderLeft>
        </Styled.HeaderContainer>
        <Styled.NoRecordContainer>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Table size={48} color="rgb(128, 128, 128)" />
            </Grid>
            <Grid item>
              <Styled.NoRecordText>Invalid URL Parameters</Styled.NoRecordText>
            </Grid>
            <Grid item>
              <Styled.NoRecordSubText>
                {urlValidation.error}
              </Styled.NoRecordSubText>
            </Grid>
          </Grid>
        </Styled.NoRecordContainer>
      </Box>
    );
  }

  // At this point, validation is valid and params are defined
  const { rateLevel, advertiserId, partnerId } = urlValidation.params || {
    rateLevel: RateLevel.SYSTEM,
    advertiserId: undefined,
    partnerId: undefined,
  };

  const segmentDetail = data?.ttdOtsSegmentDetailById;

  const handleBackClick = () => {
    history.push(Routes.OTS_SEGMENT_MANAGER);
  };

  const handleEditAudience = () => {
    //TODO: will uncomment in future
    // if (segmentDetail?.audienceId) {
    //   history.push(
    //     `${Routes.AUDIENCE_STUDIO_EDITOR}/${segmentDetail?.audienceId}`
    //   );
    // } else {
    //   history.push(Routes.AUDIENCE_STUDIO);
    // }
  };

  if (error || (!loading && !segmentDetail)) {
    return (
      <Box>
        <Styled.HeaderContainer>
          <Styled.HeaderLeft>
            <Styled.BackButton onClick={handleBackClick}>
              <ArrowLeft size={20} />
            </Styled.BackButton>
          </Styled.HeaderLeft>
        </Styled.HeaderContainer>
        <Styled.NoRecordContainer>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <Table size={48} color="rgb(128, 128, 128)" />
            </Grid>
            <Grid item>
              <Styled.NoRecordText>
                {OTS_SGM_MNG_NO_RECORD_FOUND}
              </Styled.NoRecordText>
            </Grid>
            <Grid item>
              <Styled.NoRecordSubText>
                {OTS_SGM_MNG_SEGMENT_NOT_FOUND_MESSAGE}
              </Styled.NoRecordSubText>
            </Grid>
          </Grid>
        </Styled.NoRecordContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Styled.HeaderContainer>
        <Styled.HeaderLeft>
          <Styled.BackButtonRow>
            <Styled.BackButton onClick={handleBackClick}>
              <ArrowLeft size={26} />
            </Styled.BackButton>
            {loading ? (
              <Styled.SegmentNamePlaceholder>
                <GhostLoader height="30px" width="500px" />
              </Styled.SegmentNamePlaceholder>
            ) : (
              <Styled.SegmentName>
                {segmentDetail?.segmentName || NO_DATA}
              </Styled.SegmentName>
            )}
          </Styled.BackButtonRow>
          {loading ? (
            <Styled.DescriptionPlaceholder>
              <GhostLoader height="20px" width="800px" />
            </Styled.DescriptionPlaceholder>
          ) : (
            <Styled.Description>
              {segmentDetail?.description ||
                OTS_SGM_MNG_NO_DESCRIPTION_AVAILABLE}
            </Styled.Description>
          )}
        </Styled.HeaderLeft>
        <Styled.HeaderRight>
          <Button
            variant="contained"
            onClick={handleEditAudience}
            disabled={loading}
            endIcon={
              <KeyboardDoubleArrowRightOutlined style={{ fontSize: '24px' }} />
            }
            style={{ fontSize: '16px' }}
          >
            {OTS_SGM_MNG_EDIT_AUDIENCE}
          </Button>
        </Styled.HeaderRight>
      </Styled.HeaderContainer>
      <Styled.ContentContainer>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <OtsSegmentDetailStatCards
              segmentDetail={segmentDetail}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <OtsSegmentSizeRevenueChart
              segmentId={segmentId}
              rateLevel={rateLevel}
              advertiserId={advertiserId || undefined}
              partnerId={partnerId || undefined}
            />
          </Grid>
        </Grid>
      </Styled.ContentContainer>
    </Box>
  );
};

export default OtsSegmentDetail;
